import { useState, useMemo } from 'react';
import { useSeoMeta } from '@unhead/react';
import { Link, useNavigate } from 'react-router-dom';
import type { NostrEvent } from '@nostrify/nostrify';
import { useAuthor } from '@/hooks/useAuthor';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useStreamingQuery } from '@/hooks/useStreamingQuery';
import { IssueCard } from '@/components/IssueCard';
import { UploadIssueDialog } from '@/components/UploadIssueDialog';
import { EditMagazineDialog } from '@/components/EditMagazineDialog';
import { ConfirmDeleteDialog } from '@/components/ConfirmDeleteDialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Card, CardContent } from '@/components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { ArrowLeft, BookOpen, Globe, Loader2, Plus, MoreVertical, Pencil, Trash2, Copy } from 'lucide-react';
import { useToast } from '@/hooks/useToast';
import { KIND_MAGAZINE, KIND_MAGAZINE_ISSUE } from '@/lib/pressstrKinds';
import { nip19 } from 'nostr-tools';

interface MagazinePageProps {
  pubkey: string;
  identifier: string;
}

export function MagazinePage({ pubkey, identifier }: MagazinePageProps) {
  const { user } = useCurrentUser();
  const navigate = useNavigate();
  const [uploadIssueOpen, setUploadIssueOpen] = useState(false);
  const [editMagazineOpen, setEditMagazineOpen] = useState(false);
  const [deleteMagazineOpen, setDeleteMagazineOpen] = useState(false);
  const { toast } = useToast();

  const magFilters = useMemo(
    () => [{ kinds: [KIND_MAGAZINE], authors: [pubkey], '#d': [identifier], limit: 1 }],
    [pubkey, identifier],
  );
  const { events: magEvents, isLoading } = useStreamingQuery(magFilters, ['magazine', pubkey, identifier]);

  const magazine: NostrEvent | undefined = magEvents[0];
  const author = useAuthor(pubkey);
  const authorMeta = author.data?.metadata;

  const magTitle = magazine?.tags.find(([n]) => n === 'title')?.[1] ?? 'Magazine';
  const magImage = magazine?.tags.find(([n]) => n === 'image')?.[1];
  const magSummary = magazine?.tags.find(([n]) => n === 'summary')?.[1];
  const magWebsite = magazine?.tags.find(([n]) => n === 'url')?.[1];
  const magLang = magazine?.tags.find(([n]) => n === 'l')?.[1];
  const topics = magazine?.tags.filter(([n]) => n === 't').map(([, v]) => v) ?? [];

  const magCoord = `${KIND_MAGAZINE}:${pubkey}:${identifier}`;

  // Stream issues as they arrive from relays; only start once the magazine is loaded
  const issueFilters = useMemo(
    () => [{ kinds: [KIND_MAGAZINE_ISSUE], '#a': [magCoord], limit: 200 }],
    [magCoord],
  );
  const { events: issues, isLoading: issuesLoading, isStreaming: issuesStreaming } =
    useStreamingQuery(issueFilters, ['magazine-issues', magCoord, !!magazine]);

  const sortedIssues = [...issues].sort((a, b) => {
    const aTs = parseInt(a.tags.find(([n]) => n === 'published_at')?.[1] ?? String(a.created_at));
    const bTs = parseInt(b.tags.find(([n]) => n === 'published_at')?.[1] ?? String(b.created_at));
    return bTs - aTs;
  });

  const npub = nip19.npubEncode(pubkey);
  const naddr = nip19.naddrEncode({ kind: KIND_MAGAZINE, pubkey, identifier });
  const isOwner = user?.pubkey === pubkey;

  const copyNaddr = async () => {
    await navigator.clipboard.writeText(naddr).catch(() => {});
    toast({ title: 'Copied!', description: 'naddr copied to clipboard.' });
  };

  useSeoMeta({
    title: `${magTitle} — PressStr`,
    description: magSummary ?? `Read ${magTitle} on PressStr`,
  });

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background">
        <div className="container mx-auto px-4 py-6 space-y-6 max-w-5xl">
          <Skeleton className="h-8 w-32" />
          <div className="flex flex-col sm:flex-row gap-6">
            <Skeleton className="w-32 sm:w-40 aspect-[2/3] rounded-xl shrink-0 mx-auto sm:mx-0" />
            <div className="flex-1 space-y-3">
              <Skeleton className="h-8 w-3/4" />
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-2/3" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!magazine) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="border-dashed max-w-md w-full">
          <CardContent className="py-16 text-center">
            <BookOpen className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
            <p className="text-muted-foreground">Magazine not found.</p>
            <Button asChild className="mt-4" variant="outline">
              <Link to="/"><ArrowLeft className="w-4 h-4 mr-2" />Back to Home</Link>
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Back nav */}
      <div className="border-b sticky top-0 bg-background/95 backdrop-blur z-30">
        <div className="container mx-auto px-4 h-12 flex items-center justify-between">
          <Button variant="ghost" size="sm" asChild>
            <Link to="/"><ArrowLeft className="w-4 h-4 mr-1" />Back</Link>
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm">
                <MoreVertical className="w-4 h-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={copyNaddr}>
                <Copy className="w-4 h-4 mr-2" />Copy naddr
              </DropdownMenuItem>
              {isOwner && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => setUploadIssueOpen(true)}>
                    <Plus className="w-4 h-4 mr-2" />Upload Issue
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => setEditMagazineOpen(true)}>
                    <Pencil className="w-4 h-4 mr-2" />Edit Magazine
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => setDeleteMagazineOpen(true)} className="text-destructive focus:text-destructive">
                    <Trash2 className="w-4 h-4 mr-2" />Delete Magazine
                  </DropdownMenuItem>
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      <div className="container mx-auto px-4 py-6 max-w-5xl">
        {/* Magazine header */}
        <div className="flex flex-col sm:flex-row gap-5 sm:gap-6 mb-8">
          {/* Cover */}
          <div className="w-32 sm:w-40 aspect-[2/3] rounded-xl overflow-hidden bg-gradient-to-br from-slate-100 to-slate-200 dark:from-slate-800 dark:to-slate-900 shrink-0 mx-auto sm:mx-0 shadow-md">
            {magImage ? (
              <img src={magImage} alt={magTitle} className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full flex items-center justify-center">
                <BookOpen className="w-12 h-12 text-slate-400" />
              </div>
            )}
          </div>

          {/* Info */}
          <div className="flex-1 min-w-0 space-y-3 text-center sm:text-left overflow-hidden">
            <div className="flex flex-wrap gap-2 justify-center sm:justify-start">
              {magLang && <Badge variant="outline">{magLang.toUpperCase()}</Badge>}
              {topics.slice(0, 5).map(t => (
                <Badge key={t} variant="secondary">#{t}</Badge>
              ))}
            </div>
            <h1 className="text-2xl sm:text-3xl font-bold">{magTitle}</h1>
            {magSummary && <p className="text-muted-foreground break-words">{magSummary}</p>}
            {magazine.content && (
              <p className="text-muted-foreground text-sm break-all overflow-hidden line-clamp-6">{magazine.content}</p>
            )}

            {/* Publisher */}
            <div className="flex items-center gap-2 pt-1 justify-center sm:justify-start">
              <Link to={`/${npub}`} className="shrink-0">
                <Avatar className="w-7 h-7 hover:opacity-80 transition-opacity">
                  <AvatarImage src={authorMeta?.picture} />
                  <AvatarFallback>{authorMeta?.name?.[0] ?? 'P'}</AvatarFallback>
                </Avatar>
              </Link>
              <div className="text-left">
                <Link to={`/${npub}`} className="text-sm font-medium leading-none hover:text-primary transition-colors">
                  {authorMeta?.name ?? 'Publisher'}
                </Link>
                <p className="text-xs text-muted-foreground font-mono">{npub.slice(0, 20)}…</p>
              </div>
            </div>

            <div className="flex flex-wrap gap-2 pt-1 justify-center sm:justify-start">
              {magWebsite && (
                <Button size="sm" variant="outline" asChild>
                  <a href={magWebsite} target="_blank" rel="noopener noreferrer">
                    <Globe className="w-4 h-4 mr-1" />Website
                  </a>
                </Button>
              )}
              {isOwner && (
                <Button size="sm" onClick={() => setUploadIssueOpen(true)}>
                  <Plus className="w-4 h-4 mr-1" />Upload Issue
                </Button>
              )}
            </div>
          </div>
        </div>

        {/* Issues */}
        <div>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg sm:text-xl font-semibold flex items-center gap-2">
              Issues ({sortedIssues.length})
              {issuesStreaming && (
                <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
              )}
            </h2>
            {isOwner && (
              <Button size="sm" variant="outline" onClick={() => setUploadIssueOpen(true)}>
                <Plus className="w-4 h-4 mr-1" />Add Issue
              </Button>
            )}
          </div>

          {issuesLoading ? (
            <div className="grid gap-4 grid-cols-2 sm:grid-cols-3 lg:grid-cols-4">
              {[1, 2, 3, 4].map(i => (
                <div key={i} className="space-y-2">
                  <Skeleton className="w-full aspect-[2/3] rounded-lg" />
                  <Skeleton className="h-3 w-3/4" />
                  <Skeleton className="h-3 w-full" />
                </div>
              ))}
            </div>
          ) : sortedIssues.length === 0 && !issuesStreaming ? (
            <Card className="border-dashed">
              <CardContent className="py-12 text-center">
                <p className="text-muted-foreground">No issues published yet.</p>
                {isOwner && (
                  <Button className="mt-4" onClick={() => setUploadIssueOpen(true)}>
                    <Plus className="w-4 h-4 mr-2" />Upload First Issue
                  </Button>
                )}
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-4 grid-cols-2 sm:grid-cols-3 lg:grid-cols-4">
              {sortedIssues.map((e: NostrEvent) => (
                <IssueCard key={e.id} event={e} onDeleted={() => {}} />
              ))}
            </div>
          )}
        </div>
      </div>

      <UploadIssueDialog open={uploadIssueOpen} onOpenChange={setUploadIssueOpen} magazine={magazine} onSuccess={() => {}} />
      <EditMagazineDialog open={editMagazineOpen} onOpenChange={setEditMagazineOpen} magazine={magazine} onSuccess={() => {}} />
      <ConfirmDeleteDialog
        open={deleteMagazineOpen}
        onOpenChange={setDeleteMagazineOpen}
        eventId={magazine.id}
        label="Magazine"
        onSuccess={() => navigate('/')}
      />
    </div>
  );
}
